import { DrivingDay } from "../../../../../../API/drivingDay";
import React, { useCallback, useEffect, useRef } from "react";
import { usePhotosDrivingDay } from "../../_field_id/usePhotosDrivingDay";
import { useSensorImageNotice } from "../../_field_id/useSensorImageNotice";
import { useTranslation } from "react-i18next";
import styles from "./../ListItem.module.css";
import { MenuItem, Select } from "@mui/material";
import DisplayDate from "../../../../../../Component/DisplayDate/DisplayDate";
import LayerListItem from "./LayerListItem";
import { isSingleDayDrive } from "./match-driving-day";

type PhotoSelectProps = {
  drivingDays: DrivingDay[];
  drivingDayId?: string;
  resultSetSelected: boolean;
};

export default function PhotoSelect({
  drivingDays,
  drivingDayId,
  resultSetSelected,
}: PhotoSelectProps): React.ReactNode {
  const [photosDrivingDay, setPhotosDrivingDay] = usePhotosDrivingDay();
  const setNoImagesForDate = useSensorImageNotice(
    (state) => state.setNoImagesForDate,
  );
  const clearNotices = useSensorImageNotice((state) => state.clearNotices);
  const { t } = useTranslation();

  const active = photosDrivingDay !== null;
  const alignedIdRef = useRef(active ? drivingDayId : undefined);

  const alignToResult = useCallback(() => {
    if (!drivingDays.length) {
      return;
    }
    const match = drivingDays.find((day) => day.id === drivingDayId);
    setNoImagesForDate(resultSetSelected && !match);
    setPhotosDrivingDay(match ? match.id : drivingDays[0].id);
  }, [
    drivingDays,
    drivingDayId,
    resultSetSelected,
    setNoImagesForDate,
    setPhotosDrivingDay,
  ]);

  useEffect(() => {
    if (active && alignedIdRef.current !== drivingDayId) {
      alignedIdRef.current = drivingDayId;
      alignToResult();
    }
  }, [active, drivingDayId, alignToResult]);

  if (!drivingDays.length) {
    return null;
  }

  return (
    <LayerListItem
      label={t("Tree photos")}
      active={active}
      onActivate={() => {
        if (active) {
          setPhotosDrivingDay(null);
          alignedIdRef.current = undefined;
          clearNotices();
          return;
        }
        alignedIdRef.current = drivingDayId;
        alignToResult();
      }}
    >
      <div className={styles.listItemBox}>
        <div className="vspace">
          <Select
            fullWidth
            value={photosDrivingDay ?? ""}
            onChange={(event) => {
              setPhotosDrivingDay(event.target.value);
            }}
          >
            {drivingDays.map((drivingDay: DrivingDay) => {
              const singleDay = isSingleDayDrive(drivingDay);

              return (
                <MenuItem value={drivingDay.id} key={drivingDay.id}>
                  <DisplayDate date={drivingDay.day_start_time} />
                  {!singleDay && (
                    <>
                      {"\u00a0-\u00a0"}
                      <DisplayDate date={drivingDay.day_end_time} />
                    </>
                  )}
                </MenuItem>
              );
            })}
          </Select>
        </div>
      </div>
    </LayerListItem>
  );
}
